import { HttpParams } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, ElementRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  Alert, AlertNote, AlertNoteForCreation, AlertsRepositoryServiceService, ConfirmDialogComponent, Corporation,
  CorporationRepositoryService, EnumeratedTypesRepositoryService, eAlertSeverity, eAlertStatus, Order, OrderRepositoryService,
  PatchDocument, User, UserRepositoryService, ALERT_SEVERITY_OPTIONS, ALERT_STATUS_OPTIONS, severityClass, statusClass,
  trackedEntityDisplayName
} from 'reg-hub-common';
import { EnvironmentUrlService } from '../../../services/environment-url/environment-url.service';
import { TranslocoService } from '@jsverse/transloco';

// The client portal's alert page, tuned for admins: which corporation it's for and who's assigned (read-only),
// no Actions or assignment, and severity/status stay editable as they were.
@Component({
  selector: 'app-alert-info',
  templateUrl: './alert-info.component.html',
  styleUrl: './alert-info.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AlertInfoComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('noteField') private noteField?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('severitySelect') private severitySelect?: MatSelect;
  @ViewChild('statusSelect') private statusSelect?: MatSelect;

  isLoading = true;
  loadError = false;
  isSaving = false;
  isSendingNote = false;
  deletingNoteID: string | null = null;

  alert!: Alert;
  corporation: Corporation | null = null;
  alertTypeName = '';
  orders: { orderID: string, order: Order | null }[] = [];
  newNote = '';
  liveStatus = '';

  alertSeverities = ALERT_SEVERITY_OPTIONS;
  alertStatuses = ALERT_STATUS_OPTIONS;

  // The corporation's users by ID; anyone else who wrote a note is RegHub staff
  private corporationUsers = new Map<string, User>();
  private currentUser: User | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private alertsRepo: AlertsRepositoryServiceService,
    private corporationsRepo: CorporationRepositoryService,
    private usersRepo: UserRepositoryService,
    private ordersRepo: OrderRepositoryService,
    private enumeratedTypesRepo: EnumeratedTypesRepositoryService,
    private environment: EnvironmentUrlService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar) { }

  ngOnInit(): void {
    this.activatedRoute.paramMap.pipe(
      map(params => params.get('alertID')!),
      switchMap(alertID => {
        this.isLoading = true;
        this.loadError = false;
        return this.alertsRepo.getAlertAdmin(alertID);
      }),
      switchMap(alert => {
        this.alert = alert;
        this.alert.notes = [...(alert.notes ?? [])].sort((a, b) => new Date(a.added).getTime() - new Date(b.added).getTime());

        const userParams = new HttpParams().set('PageSize', 100).set('CorporationID', alert.corporationID);
        return forkJoin({
          corporation: this.corporationsRepo.getCorporation(alert.corporationID).pipe(catchError(() => of(null))),
          users: this.usersRepo.adminGetUsers(userParams).pipe(catchError(() => of([] as User[]))),
          currentUser: this.usersRepo.getAuthenticatedUser().pipe(catchError(() => of(null))),
          alertTypes: this.enumeratedTypesRepo.getAlertTypes(this.environment.urlAddress).pipe(catchError(() => of([]))),
          orders: this.loadOrders(alert.trackedAlertEntity?.orderIDs ?? [])
        });
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ corporation, users, currentUser, alertTypes, orders }) => {
        this.corporation = corporation;
        this.corporationUsers = new Map(users.map(u => [u.id, u]));
        this.currentUser = currentUser;
        this.alertTypeName = alertTypes.find(t => t.id === this.alert.alertTypeID)?.name ?? this.alert.alertTypeID;
        this.orders = orders;
        this.isLoading = false;
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
      }
    });
  }

  // A failed lookup still shows the order ID
  private loadOrders(orderIDs: string[]): Observable<{ orderID: string, order: Order | null }[]> {
    if (orderIDs.length === 0) {
      return of([]);
    }

    return forkJoin(orderIDs.map(orderID =>
      this.ordersRepo.getOrder('orders', orderID).pipe(
        map(order => ({ orderID, order: order as Order | null })),
        catchError(() => of({ orderID, order: null })))
    ));
  }

  get severityClass(): string {
    return severityClass(this.alert.alertSeverityID);
  }

  get statusClass(): string {
    return statusClass(this.alert.alertStatusID);
  }

  get subject(): string {
    return trackedEntityDisplayName(this.alert.trackedAlertEntity);
  }

  // Older APIs don't send isTracked, so fall back to the end date (none means ongoing)
  get isTracked(): boolean {
    const entity = this.alert.trackedAlertEntity;
    return entity?.isTracked ?? (!entity?.trackingEndDate || new Date(entity.trackingEndDate) > new Date());
  }

  get assignedUserName(): string {
    const id = this.alert.assignedUserID;
    if (!id) {
      return this.transloco.translate('alertInfo.unassigned');
    }
    const user = this.corporationUsers.get(id);
    return user ? this.fullNameWithUserName(user) : this.transloco.translate('common.messages.unknownUser');
  }

  private fullNameWithUserName(user: User): string {
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return fullName ? `${fullName} (${user.userName})` : user.userName;
  }

  // Client users by name; the signed-in admin by name; any other RegHub staff as "RegHub"
  noteAuthor(note: AlertNote): string {
    const user = this.corporationUsers.get(note.createdByUserID)
      ?? (note.createdByUserID === this.currentUser?.id ? this.currentUser : null);
    if (!user) {
      return 'RegHub';
    }
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.userName;
  }

  // The API adds activity entries when the alert changes, so show its latest notes
  private setNotes(notes: AlertNote[] | undefined) {
    if (notes) {
      this.alert.notes = [...notes].sort((a, b) => new Date(a.added).getTime() - new Date(b.added).getTime());
    }
  }

  isRegHubNote(note: AlertNote): boolean {
    return !this.corporationUsers.has(note.createdByUserID);
  }


  // Severity and status stay editable for admins. No success message: the pill already shows the change.
  onSeverityChange(severity: eAlertSeverity) {
    this.applyPatch([{ op: 'replace', path: '/alertSeverityID', value: severity }], this.transloco.translate('alertInfo.severityIsNow', { severity }));
  }

  onStatusChange(status: eAlertStatus) {
    this.applyPatch([{ op: 'replace', path: '/alertStatusID', value: status }], this.transloco.translate('alertInfo.statusIsNow', { status }));
  }

  private applyPatch(patch: PatchDocument[], doneMessage: string) {
    this.isSaving = true;

    this.alertsRepo.patchAlert(this.alert.corporationID, this.alert.id, patch).subscribe({
      next: updated => {
        this.alert.alertSeverityID = updated.alertSeverityID;
        this.alert.alertStatusID = updated.alertStatusID;
        this.setNotes(updated.notes);
        this.isSaving = false;
        this.liveStatus = doneMessage;
      },
      error: () => {
        this.isSaving = false;
        // [value] hasn't changed, so Angular won't push it back into the selects; undo the pick by hand
        if (this.severitySelect) {
          this.severitySelect.value = this.alert.alertSeverityID;
        }
        if (this.statusSelect) {
          this.statusSelect.value = this.alert.alertStatusID;
        }
        this.showError(this.transloco.translate('common.messages.unableToUpdateAlertPleaseTry'));
      }
    });
  }

  sendNote() {
    const note = this.newNote.trim();
    if (!note) {
      return;
    }

    this.isSendingNote = true;
    const noteForCreation: AlertNoteForCreation = { note };

    this.alertsRepo.createAlertNote(this.alert.corporationID, this.alert.id, noteForCreation).subscribe({
      next: created => {
        this.alert.notes = [...this.alert.notes, created];
        this.newNote = '';
        this.isSendingNote = false;
        this.liveStatus = this.transloco.translate('alertInfo.noteAdded');
      },
      error: () => {
        this.isSendingNote = false;
        this.showError(this.transloco.translate('alertInfo.unableToAddNotePleaseTry'));
      }
    });
  }

  // Admins can remove any note (e.g. one posted in error), so always confirm first
  deleteNote(note: AlertNote) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.transloco.translate('corporations.deleteNote'),
        confirmationPrompt: this.transloco.translate('alertInfo.deleteThisNoteFromThisCan', { note: this.noteAuthor(note) }),
        positiveResponseLabel: this.transloco.translate('common.buttons.delete'),
        negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
      },
      width: '800px'
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.confirmDeleteNote(note);
      }
    });
  }

  private confirmDeleteNote(note: AlertNote) {
    this.deletingNoteID = note.id;

    this.alertsRepo.deleteAlertNote(this.alert.corporationID, this.alert.id, note.id).subscribe({
      next: () => {
        this.alert.notes = this.alert.notes.filter(n => n.id !== note.id);
        this.deletingNoteID = null;
        this.liveStatus = this.transloco.translate('common.messages.noteDeleted');
        // The focused delete button is gone with its note; land on the composer instead of <body>
        this.noteField?.nativeElement.focus();
      },
      error: () => {
        this.deletingNoteID = null;
        this.showError(this.transloco.translate('common.messages.unableToDeleteNotePleaseTry'));
      }
    });
  }

  navigateToLogs() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['logs'], { queryParams: { tab: 'changes', entityID: this.alert.id, entityName: 'Alert' } })
    );

    window.open(url, '_blank');
  }

  private showError(message: string) {
    this.snackBar.open(message, this.transloco.translate('common.buttons.close'), { duration: 5000 });
  }
}
